import { useCallback, useMemo } from 'react';

import { noop } from 'ui-components/default-props/noop';

import { StepType } from '../../../../../components/csv-import/data/types/step-type';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';
import { Segment } from '../../../../../services/analytics-services/segment';
import { bulkUpdateConnections } from '../../../../settings/inventory-sources/api';
import { InventoryConnectionTerminusTypeEnum } from '../../../../settings/inventory-sources/enums';
import { INSURANCE_OPTION_UPDATED, FTU_PHONE_CAPTURE_SKIP } from '../../../../../constants/analytics-events';
import { FTU_SHIP_FROM_SAVE, FTU_WIZARD_PAGE, FTU_WIZARD_STEPS, NAVIGATION_ACTIONS } from '../constants';
import { INVENTORY_MANAGEMENT_OPTIONS } from '../context/inventory-sync-context/inventory-management-options';
import { INVENTORY_MANUAL_MANAGEMENT_SUBSTEPS } from '../context/inventory-sync-context/inventory-manual-management-substeps';
import { todoCardIds } from '../../todo-cards/todo-card-ids';

const handleInventorySetupNavigation = async (stepData) => {
  const { inventoryManagementOption, inventorySubstep, latestImport, selectedConnections } = stepData;

  if (inventoryManagementOption === INVENTORY_MANAGEMENT_OPTIONS.noManagement) {
    if (selectedConnections?.length > 0) {
      stepData.updateConfig();
    }
    return NAVIGATION_ACTIONS.PROCEED;
  }

  if (inventoryManagementOption === INVENTORY_MANAGEMENT_OPTIONS.shopifyIMS) {
    // Find Shopify connections and set them as Source
    const shopifyConnections = stepData?.sources?.filter((connection) => connection.marketplaceId === MarketplaceIds.Shopify) ?? [];

    if (shopifyConnections.length > 0) {
      const updateConnectionRequests = shopifyConnections.map((connection) => ({
        connectionId: connection.connectionId,
        terminusType: InventoryConnectionTerminusTypeEnum.Source,
      }));
      await bulkUpdateConnections(updateConnectionRequests);
    }

    return NAVIGATION_ACTIONS.PROCEED;
  }

  stepData.confirmInventoryManagementOption();

  if (inventoryManagementOption === INVENTORY_MANAGEMENT_OPTIONS.externalIMS) {
    if (!inventorySubstep) {
      stepData.updateInventorySubstep(INVENTORY_MANUAL_MANAGEMENT_SUBSTEPS.imsConnection);
      return NAVIGATION_ACTIONS.STAY;
    }

    if (inventorySubstep === INVENTORY_MANUAL_MANAGEMENT_SUBSTEPS.imsConnection) {
      return NAVIGATION_ACTIONS.PROCEED;
    }
  }

  if (inventoryManagementOption === INVENTORY_MANAGEMENT_OPTIONS.manualManagement) {
    return NAVIGATION_ACTIONS.PROCEED;
  }

  if (inventoryManagementOption === INVENTORY_MANAGEMENT_OPTIONS.spreadsheet) {
    if (latestImport.status) {
      if (inventorySubstep === INVENTORY_MANUAL_MANAGEMENT_SUBSTEPS.importResultAndInventorySync) {
        stepData.updateConfig();
        return NAVIGATION_ACTIONS.PROCEED;
      }
      stepData.updateInventorySubstep(INVENTORY_MANUAL_MANAGEMENT_SUBSTEPS.importResultAndInventorySync);
      return NAVIGATION_ACTIONS.STAY;
    }

    stepData.initImport(stepData.open);
    return NAVIGATION_ACTIONS.STAY;
  }

  return NAVIGATION_ACTIONS.STAY;
};

const stepNavigationHandlers = {
  [FTU_WIZARD_STEPS.ORDERS]: {
    canProceed: (stepData) => stepData.hasStores || (stepData.hasCsvFile && Boolean(stepData.selectedOrderStoreGuid)),
    handleNext: (stepData) => {
      if (stepData.hasStores) {
        return NAVIGATION_ACTIONS.PROCEED;
      }

      if (stepData.hasManualOrder) {
        return NAVIGATION_ACTIONS.PROCEED;
      }

      if (stepData.hasCsvFile && stepData.selectedOrderStoreGuid) {
        const currentStepId = stepData.orderCurrentStepId?.();

        if (currentStepId === StepType.IMPORT_IS_PROCESSING) {
          return NAVIGATION_ACTIONS.PROCEED;
        }

        if (currentStepId === StepType.CREATE_MAPPING && stepData.orderOnImport) {
          stepData.orderOnImport(noop);
          return NAVIGATION_ACTIONS.STAY;
        }

        if (stepData.orderInitImport) {
          stepData.orderInitImport(noop);
          return NAVIGATION_ACTIONS.STAY;
        }
      }

      return NAVIGATION_ACTIONS.STAY;
    },
  },

  [FTU_WIZARD_STEPS.SHIP_FROM]: {
    canProceed: (stepData) => stepData.hasShipFroms || stepData.isFormValid,
    handleNext: async (stepData) => {
      if (stepData.hasShipFroms) {
        return NAVIGATION_ACTIONS.PROCEED;
      }

      if (!stepData.saveFunction) {
        return NAVIGATION_ACTIONS.STAY;
      }

      try {
        const result = await stepData.saveFunction();
        if (result) {
          Segment.trackEvent(FTU_SHIP_FROM_SAVE, {
            metadata: result,
          });
        }

        return NAVIGATION_ACTIONS.STAY;
      } catch {
        return NAVIGATION_ACTIONS.ERROR;
      }
    },
  },

  [FTU_WIZARD_STEPS.CARRIER]: {
    canProceed: () => true,
    handleNext: async (stepData) => {
      await stepData.refundAssistSaveFunction();
      return NAVIGATION_ACTIONS.PROCEED;
    },
  },

  [FTU_WIZARD_STEPS.PHONE_NUMBER]: {
    canProceed: (stepData) => !stepData.saveFunction || stepData.isFormValid,
    handleNext: async (stepData) => {
      if (stepData.saveFunction && stepData.isFormValid) {
        try {
          await stepData.saveFunction();

          // Mark PHONE_NUMBER_CAPTURE todo as completed after successful save
          const { todosInLocalState, updateTodosWithSave } = stepData;
          if (updateTodosWithSave && todosInLocalState) {
            const updatedTodos = todosInLocalState.map((todo) => {
              if (todo.id === todoCardIds.PHONE_NUMBER_CAPTURE) {
                return { ...todo, isCompleted: true };
              }
              return todo;
            });
            updateTodosWithSave(updatedTodos);
          }

          return NAVIGATION_ACTIONS.PROCEED;
        } catch {
          return NAVIGATION_ACTIONS.ERROR;
        }
      }
      return NAVIGATION_ACTIONS.PROCEED;
    },
    handleSkip: () => {
      Segment.trackEvent(FTU_PHONE_CAPTURE_SKIP);
      return NAVIGATION_ACTIONS.PROCEED;
    },
  },

  [FTU_WIZARD_STEPS.DISCOUNTED_RATES]: {
    canProceed: () => true,
    handleNext: (stepData) => {
      if (stepData.insuranceSelection) {
        Segment.trackEvent(INSURANCE_OPTION_UPDATED, {
          page: FTU_WIZARD_PAGE,
          optionSelected: stepData.insuranceSelection,
        });
      }
      return NAVIGATION_ACTIONS.PROCEED;
    },
  },

  [FTU_WIZARD_STEPS.INVENTORY_SETUP]: {
    canProceed: (stepData) => stepData.hasRequiredData,
    handleNext: handleInventorySetupNavigation,
  },

  [FTU_WIZARD_STEPS.RETURNS]: {
    canProceed: (stepData) => {
      const { merchantHandlesReturns, hasAnyInitiationOptionSelected } = stepData;

      if (!merchantHandlesReturns) {
        return true;
      }

      return hasAnyInitiationOptionSelected;
    },
    handleNext: () => NAVIGATION_ACTIONS.PROCEED,
  },
};

const defaultNavigation = {
  canProceed: () => true,
  handleNext: () => NAVIGATION_ACTIONS.PROCEED,
};

export const useStepNavigation = (stepKey, stepData) => {
  const navigationHandler = stepNavigationHandlers[stepKey] || defaultNavigation;

  const canProceed = useMemo(() => {
    return navigationHandler.canProceed(stepData);
  }, [stepData, navigationHandler]);

  const handleNext = useCallback(async () => {
    return await navigationHandler.handleNext(stepData);
  }, [stepData, navigationHandler]);

  return {
    canProceed,
    handleNext,
  };
};
